import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { DeleteOutline as DeleteIcon, ImageOutlined as ImageIcon } from '@mui/icons-material';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import {
  REMOVE_ORGANIZATION_COVER,
  REMOVE_ORGANIZATION_LOGO,
  UPLOAD_ORGANIZATION_COVER,
  UPLOAD_ORGANIZATION_LOGO
} from 'api/graphql/organization';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';

// Debe ir de la mano con `@rules(apply: ["max:10000"])` del schema (KB). Validar aquí evita el
// viaje inútil: una foto de portada sale del móvil pesando 8-12 MB con facilidad.
const MAX_COVER_BYTES = 10 * 1024 * 1024;

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

// Las dos imágenes se ENSEÑAN distinto porque se USAN distinto: la portada llena la banda del
// hero (por eso se previsualiza recortada, 3:1, como se va a ver de verdad) y el logo se muestra
// entero sobre ella (por eso va contenido, sobre un tablero a cuadros que delata la transparencia).
const VARIANTS = {
  cover: { ratio: '3 / 1', fit: 'cover' },
  logo: { ratio: '5 / 2', fit: 'contain' }
};

// Cuadros de transparencia: sin ellos, un logo con fondo transparente sobre una superficie
// oscura parece tener fondo negro y nadie sabe si subió bien.
const checkerSx = {
  backgroundImage:
    'linear-gradient(45deg, rgba(128,128,128,0.18) 25%, transparent 25%), linear-gradient(-45deg, rgba(128,128,128,0.18) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(128,128,128,0.18) 75%), linear-gradient(-45deg, transparent 75%, rgba(128,128,128,0.18) 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px'
};

const frameSx = {
  position: 'relative',
  width: '100%',
  borderRadius: '14px',
  border: '1px solid',
  borderColor: 'hairline.main',
  backgroundColor: 'surface.raised',
  overflow: 'hidden'
};

/**
 * Portada de la empresa: la imagen que se usa como fondo del hero de su panel "Prometeo".
 * Sin portada, todas las empresas comparten la misma foto genérica de OTRIX.
 *
 * Vive en el DETALLE y no en el formulario de creación a propósito: la subida necesita el id de
 * la organización, que en el alta todavía no existe.
 */
function OrganizationCoverField({ organizationId, coverUrl = null, onChanged, variant = 'cover' }) {
  const { t } = useTranslation(['organizations']);
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isLogo = variant === 'logo';
  const shape = VARIANTS[variant] || VARIANTS.cover;
  // Textos por variante: decirle "portada" al logo confundiría a quien sube las imágenes, que es
  // justo la persona que tiene que distinguirlas.
  const copy = isLogo
    ? {
        label: t('logoLabel', { defaultValue: 'Logo de la empresa' }),
        alt: t('logoAlt', { defaultValue: 'Logo de la empresa' }),
        empty: t('logoEmpty', { defaultValue: 'Sin logo: en el panel solo se ve la portada.' }),
        change: t('logoChange', { defaultValue: 'Cambiar logo' }),
        upload: t('logoUpload', { defaultValue: 'Subir logo' }),
        hint: t('logoHint', { defaultValue: 'PNG con fondo transparente. Se muestra entero, sin recortar.' }),
        removeTitle: t('logoRemoveTitle', { defaultValue: 'Quitar logo' }),
        removeMessage: t('logoRemoveMessage', { defaultValue: 'En el panel de la empresa quedará solo la portada.' }),
        tooBig: t('logoTooBig', { defaultValue: 'El logo supera los 10 MB. Usa una versión más liviana en PNG.' }),
        uploadError: t('logoUploadError', { defaultValue: 'No pudimos subir el logo. Intenta de nuevo.' }),
        removeError: t('logoRemoveError', { defaultValue: 'No pudimos quitar el logo. Intenta de nuevo.' })
      }
    : {
        label: t('coverLabel', { defaultValue: 'Portada de la empresa' }),
        alt: t('coverAlt', { defaultValue: 'Portada de la empresa' }),
        empty: t('coverEmpty', { defaultValue: 'Sin portada: su panel usa la imagen genérica de OTRIX.' }),
        change: t('coverChange', { defaultValue: 'Cambiar portada' }),
        upload: t('coverUpload', { defaultValue: 'Subir portada' }),
        hint: t('coverHint', { defaultValue: 'Apaisada, mínimo 1200x400 px. Máximo 10 MB.' }),
        removeTitle: t('coverRemoveTitle', { defaultValue: 'Quitar portada' }),
        removeMessage: t('coverRemoveMessage', { defaultValue: 'El panel de la empresa volverá a mostrar la imagen genérica de OTRIX.' }),
        tooBig: t('coverTooBig', { defaultValue: 'La imagen supera los 10 MB. Usa una versión más liviana en JPG o PNG.' }),
        uploadError: t('coverUploadError', { defaultValue: 'No pudimos subir la portada. Intenta de nuevo.' }),
        removeError: t('coverRemoveError', { defaultValue: 'No pudimos quitar la portada. Intenta de nuevo.' })
      };

  const [uploadCover, { loading: uploading }] = useMutation(
    isLogo ? UPLOAD_ORGANIZATION_LOGO : UPLOAD_ORGANIZATION_COVER
  );
  const [removeCover, { loading: removing }] = useMutation(
    isLogo ? REMOVE_ORGANIZATION_LOGO : REMOVE_ORGANIZATION_COVER
  );
  const busy = uploading || removing;

  const notifyError = (message) =>
    dispatch(
      uiSlice.actions.enqueueSnackbar(
        createSnack({ key: `org-cover-${Date.now()}`, variant: 'error', message })
      )
    );

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    // El input se limpia SIEMPRE: si no, elegir el mismo archivo tras un error no dispara
    // `change` otra vez y parece que el botón dejó de responder.
    event.target.value = '';
    if (!file) return;

    if (file.size > MAX_COVER_BYTES) {
      notifyError(copy.tooBig);
      return;
    }

    try {
      await uploadCover({ variables: { id: organizationId, image: file } });
      onChanged();
    } catch (error) {
      notifyError(copy.uploadError);
    }
  };

  const handleRemove = async () => {
    setConfirmOpen(false);
    try {
      await removeCover({ variables: { id: organizationId } });
      onChanged();
    } catch (error) {
      notifyError(copy.removeError);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
        {copy.label}
      </Typography>

      <Box sx={{ ...frameSx, aspectRatio: shape.ratio, ...(isLogo ? checkerSx : {}) }}>
        {coverUrl ? (
          <Box
            component="img"
            src={coverUrl}
            alt={copy.alt}
            sx={{ width: '100%', height: '100%', objectFit: shape.fit, display: 'block' }}
          />
        ) : (
          <Stack
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100%', color: 'text.secondary', px: 2, textAlign: 'center' }}>
            <ImageIcon sx={{ fontSize: 30, opacity: 0.7 }} />
            <Typography variant="caption">
              {copy.empty}
            </Typography>
          </Stack>
        )}

        {busy && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(10, 10, 10, 0.55)'
            }}>
            <CircularProgress size={26} sx={{ color: 'gold.main' }} />
          </Stack>
        )}
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
        <Button
          size="small"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            color: 'gold.main',
            transition: `transform 200ms ${EASE}`,
            '&:active': { transform: 'scale(0.98)' }
          }}>
          {coverUrl ? copy.change : copy.upload}
        </Button>
        {coverUrl && (
          <Button
            size="small"
            disabled={busy}
            startIcon={<DeleteIcon sx={{ fontSize: 17 }} />}
            onClick={() => setConfirmOpen(true)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: 'sessionStatus.absent.fg',
              transition: `transform 200ms ${EASE}`,
              '&:active': { transform: 'scale(0.98)' }
            }}>
            {t('coverRemove', { defaultValue: 'Quitar' })}
          </Button>
        )}
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {copy.hint}
        </Typography>
      </Stack>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={handleFile}
      />

      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleRemove}
        title={copy.removeTitle}
        message={copy.removeMessage}
        confirmLabel={t('coverRemove', { defaultValue: 'Quitar' })}
      />
    </Box>
  );
}

OrganizationCoverField.propTypes = {
  coverUrl: PropTypes.string,
  /** 'cover' = fondo del hero (se recorta) · 'logo' = marca encima (se muestra entera). */
  // eslint-disable-next-line react/require-default-props
  variant: PropTypes.oneOf(['cover', 'logo']),
  onChanged: PropTypes.func.isRequired,
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default OrganizationCoverField;
